import { Users, Zap, Warehouse, Flame, Truck, Megaphone, Paintbrush, ClipboardCheck } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { scrollToId } from "@/App";

const WHY_IMG =
  "https://images.unsplash.com/photo-1737476997205-b3336182f215?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwaWNrbGViYWxsJTIwY291cnQlMjBhY3Rpb258ZW58MHx8fHwxNzkxNDI5ODY3fDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: Users, text: "All ages welcome" },
  { icon: Zap, text: "Pick up & play in 2 mins" },
  { icon: Warehouse, text: "Indoors or outdoors" },
  { icon: Flame, text: "Crowds love it" },
];

const DELIVERABLES = [
  {
    icon: Truck,
    title: "Delivery & Setup",
    desc: "We bring the court to you, finished with top-of-the-range JOOLA pro surfacing. Any level hard surface works — carparks, rooftops, office floors, precinct plazas. We handle every bit of logistics.",
    span: "md:col-span-2",
  },
  {
    icon: Megaphone,
    title: "Facilitation & Hosting",
    desc: "Experienced hosts run the session start to finish — beginner drills, tournaments, non-stop energy.",
    span: "",
  },
  {
    icon: Paintbrush,
    title: "Custom Branded Courts",
    desc: "Your logo on the court, in any dimension. Production lead times apply — enquire early.",
    span: "",
  },
  {
    icon: ClipboardCheck,
    title: "All We Need? A Level Surface.",
    desc: "That's it. We supply everything else: court, net, paddles, balls, delivery, setup and pack-down. Tell us the venue and we'll advise on court count and layout.",
    span: "md:col-span-2",
  },
];

export default function WhyDeliver() {
  return (
    <>
      <section id="why" className="relative py-24 lg:py-32" data-testid="why-pickleball-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt">Why pickleball</p>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              Try something different at your next event
            </h2>
            <p className="mt-6 text-base text-white/70 sm:text-lg">
              Pickleball is the fastest growing sport on the planet — fast, fun, and anyone can pick it up in
              minutes. No experience needed. Just paddles, a ball and a net, and suddenly everyone's competing,
              laughing and coming back for more.
            </p>
            <p className="mt-4 text-base text-white/70 sm:text-lg">
              Birthdays, end-of-year celebrations, council activations, corporate team days or a shopping centre
              pop-up — pickleball works for every occasion and every age group.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3" data-testid="why-pickleball-card">
              {PILLARS.map((p) => (
                <div
                  key={p.text}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-carbon px-4 py-3 transition-colors hover:border-volt/40"
                >
                  <p.icon size={18} className="shrink-0 text-volt" />
                  <span className="text-sm font-semibold text-white/85">{p.text}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl ring-1 ring-white/10">
                <img src={WHY_IMG} alt="Players rallying on a pop-up pickleball court" className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]" />
              </div>
              <div className="absolute -bottom-5 left-5 rounded-2xl border border-volt/30 bg-obsidian/90 px-5 py-4 backdrop-blur-md">
                <p className="font-mono text-xs tracking-[0.2em] text-volt">COURT FOOTPRINT</p>
                <p className="mt-1 font-display text-2xl font-extrabold uppercase text-white">13.41m × 6.10m</p>
                <p className="text-xs text-white/50">Any level hard surface</p>
                <button
                  onClick={() => scrollToId("#dimensions")}
                  data-testid="why-dimensions-link"
                  className="mt-2 font-mono text-[11px] tracking-[0.15em] text-volt hover:underline"
                >
                  SEE COURT DIMENSIONS →
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="deliver" className="relative bg-carbon py-24 lg:py-32" data-testid="deliverables-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHead eyebrow="What we deliver" title="A complete court. Anywhere you need it." testid="deliverables-heading" />
          <div className="grid gap-4 md:grid-cols-3">
            {DELIVERABLES.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.08} className={d.span}>
                <div
                  data-testid="bento-deliverable-item"
                  className="group h-full rounded-3xl border border-white/10 bg-panel p-8 transition-colors duration-300 hover:border-volt/40 lg:p-10"
                >
                  <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-volt/10 text-volt transition-colors group-hover:bg-volt group-hover:text-obsidian">
                    <d.icon size={22} />
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">{d.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60 sm:text-base">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
